import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    Users,
    UserCog,
    Loader2,
    Briefcase,
    UserPlus,
    Settings2,
} from "lucide-react";
import { toast } from "react-toastify";
import PageBreadcrumb from "../../../../shared/PageBreadCrumb";
import amsProjectService from "../../../../services/amsProjectService";

export default function AmsManageProjectResources() {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const [project, setProject] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!projectId) return;
        amsProjectService
            .getProjectById(projectId)
            .then((data) => setProject(data))
            .catch((e: any) => {
                toast.error(e?.message ?? "Failed to load project");
                navigate("/projects");
            })
            .finally(() => setLoading(false));
    }, [projectId]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
        );
    }

    if (!project) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-12 text-center">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">Project not found</p>
            </div>
        );
    }

    const auditors: any[] = project.assignedResources?.auditors ?? [];
    const projectManager = project.assignedResources?.projectManager ?? project.manager;
    const projectHead = project.assignedResources?.projectHead;

    const allMembers = [
        ...(projectManager ? [{ ...projectManager, role: "Project Manager", colorClass: "bg-teal-100 text-teal-700" }] : []),
        ...(projectHead ? [{ ...projectHead, role: "Project Head", colorClass: "bg-blue-100 text-blue-700" }] : []),
        ...auditors.map((a) => ({ ...a, role: "Auditor", colorClass: "bg-emerald-100 text-emerald-700" })),
    ];

    return (
        <div className="max-w-5xl mx-auto px-3 sm:px-4 pb-6">
            <PageBreadcrumb
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Projects", path: "/projects" },
                    {
                        name: project.projectName ?? project.name ?? "Project",
                        path: `/projects/view/${projectId}`,
                    },
                    { name: "Manage Resources", path: undefined },
                ]}
            />

            <div className="flex items-center gap-2 mb-4">
                <button
                    onClick={() => navigate(`/projects/view/${projectId}`)}
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back
                </button>
                <div className="h-4 w-px bg-slate-200" />
                <h1 className="text-lg font-bold text-slate-900">
                    Manage Resources — {project.projectName ?? project.name}
                </h1>
            </div>

            {/* Current resources summary */}
            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-4"
            >
                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-teal-600" />
                        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                            Assigned Resources ({allMembers.length})
                        </h3>
                    </div>
                    <button
                        onClick={() => navigate(`/projects/edit/${projectId}`)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded transition-colors"
                    >
                        <UserCog className="w-3 h-3" />
                        Edit in Project Form
                    </button>
                </div>
                <div className="p-3">
                    {allMembers.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {allMembers.map((member: any, i: number) => (
                                <div
                                    key={member._id ?? i}
                                    className="flex items-center gap-2.5 p-2 rounded-md border border-slate-100 bg-slate-50/50"
                                >
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-[12px] flex-shrink-0 ${member.colorClass}`}
                                    >
                                        {(member.name ?? "?").charAt(0).toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-xs font-medium text-slate-800 truncate">
                                            {member.name}
                                        </div>
                                        <div className="text-[10px] text-slate-500">{member.role}</div>
                                        {member.email && (
                                            <div className="text-[10px] text-slate-400 truncate">{member.email}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-slate-400 italic py-2">No resources assigned yet</p>
                    )}
                </div>
            </motion.div>

            {/* Coming-soon panel for full resource management */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08, duration: 0.2 }}
                className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center"
            >
                <div className="flex justify-center gap-3 mb-4 opacity-40">
                    <UserPlus className="w-8 h-8 text-teal-600" />
                    <Settings2 className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-700 mb-1">
                    Full Resource Management — Coming Soon
                </h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                    Dynamic auditor scheduling, availability tracking, and workload balancing
                    across AMS audit projects. This feature is under development.
                </p>
                <button
                    onClick={() => navigate(`/projects/edit/${projectId}`)}
                    className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md transition-colors"
                >
                    <UserCog className="w-3.5 h-3.5" />
                    Manage team from project form
                </button>
            </motion.div>
        </div>
    );
}
